import chunk from 'lodash/chunk';
import uniq from 'lodash/uniq';

import BaseApi from '@lumapps/base-api';
import { BaseApiPromise, PRIORITY, ServerListRequest, ServerListResponse } from '@lumapps/base-api/types';
import { createGetMultiBatcher } from '@lumapps/base-api/utils/createGetMultiBatcher';
import { CACHE_TYPE } from '@lumapps/cache';
import { splitArray } from '@lumapps/utils/array/splitArray';
import type { Fields } from '@lumapps/utils/types/Fields';

import { Metadata, MetadataEntity, NewMetadataPayload } from '../types';

/**
 * Metadata API.
 * https://api.lumapps.com/docs/metadata
 */
export const metadataApi = new BaseApi({ path: 'metadata', bypassMonolith: true });

export interface ListMetadataRequest extends Omit<ServerListRequest, 'maxResults'> {
    emptyParent?: boolean;
    name?: string;
    parent?: string;
    instance?: string;
    familyId?: string;
    fields?: string;
    maxResults?: number;
    more?: boolean;
    displayInFilter?: boolean;
    customContentTypeIds?: string[];
    withoutCustomContentTypes?: boolean;
    canBeAppliedTo?: MetadataEntity[];
}

/** List customer and/or instance metadata */
export const listMetadata = (params: ListMetadataRequest) =>
    metadataApi.get<ServerListResponse<Metadata>>('list', {
        // fallback on most common applicable entity "content" if not specified
        params: { canBeAppliedTo: [MetadataEntity.content], ...params },
    });

const RESTRICT_TO_METADATA_LIMIT = 150;
export interface GetMultipleMetadata {
    ids: string[];
    fields?: string;
    listKey?: string;
}

/** @deprecated use `getMultipleMetadataBatched` instead. */
export const getMultipleMetadata = (params: GetMultipleMetadata) => {
    /**
     * If the total queried `ids` is more than RESTRICT_TO_METADATA_LIMIT, the backend will return a 400
     * since it has a fixed limit on the backend. We fetch these items in batchs in order to avoid that error,
     * and return a single promise that will resolve when all those promises are finished.
     */
    if (params.ids && params.ids.length > 0) {
        if (params.ids.length > RESTRICT_TO_METADATA_LIMIT) {
            const metadatas = splitArray(uniq(params.ids), RESTRICT_TO_METADATA_LIMIT);
            const promises: BaseApiPromise<ServerListResponse<Metadata>>[] = metadatas.map((list: string[]) => {
                return getMultipleMetadata({
                    ...params,
                    ids: list,
                });
            });

            return Promise.all(promises).then((responses) => {
                const items: Metadata[] = [];

                responses.forEach((response) => {
                    const { data } = response;

                    items.push(...data.items);
                });

                /**
                 * we use all metadata and headers of the first request, and return
                 * the total items from all responses. Not the best, we are trying to find a fix
                 * on the backend side for this.
                 */
                return {
                    ...responses[0],
                    data: {
                        items,
                        cursor: '',
                        more: false,
                    },
                };
            });
        }

        /**
         * If `ids` is less than the limit, we pass in `maxResults` as the same as
         * the total `ids` to have them all.
         */
        Object.assign(params, { maxResults: params.ids.length.toString() });
    }

    return metadataApi.getCacheFirst<ServerListResponse<Metadata>>('getMulti', CACHE_TYPE.MEMORY, PRIORITY.HIGH, {
        params,
    });
};

/**
 * Create metadata category. Can be used for both instance and customer
 */
export const createMetadata = (metadata: NewMetadataPayload) => {
    return metadataApi.post('save', metadata);
};

/**
 * Update metadata category. Can be used for both instance and customer
 */
export const updateMetadata = (metadata: Metadata) => {
    return metadataApi.post('save', metadata);
};

/**
 * delete metadata category. Can be used for both instance and customer
 */
export const deleteMetadata = (metadataId: string) => {
    return metadataApi.delete('delete', { params: { uid: metadataId } });
};

/**
 * Get metadata by id batched
 */
export const getMetadataBatched = createGetMultiBatcher<Metadata>({
    getId: 'id',
    async getMulti(ids, fieldsArray, signal) {
        // Get multi metadata (chunked to avoid big requests)
        const fields = fieldsArray?.length ? `items(${fieldsArray?.join(',')})` : undefined;
        const idsChunks = chunk(ids, RESTRICT_TO_METADATA_LIMIT);
        const requests = idsChunks.map(async (ids) => {
            const maxResults = ids.length;
            const { data } = await metadataApi.get<ServerListResponse<Metadata>>('getMulti', {
                params: { ids, maxResults, fields },
                signal,
            });
            return data.items;
        });
        const responses = await Promise.all(requests);
        return responses.flat();
    },
});

/**
 * Get multiple metadata (batched with calls to `getMetadataBatched` and multiple calls to `getMultipleMetadataBatched`)
 */
export const getMultipleMetadataBatched = (ids: string[], fields: Fields<Metadata>, signal?: AbortSignal) => {
    return Promise.all(ids.map((id) => getMetadataBatched(id, fields, signal)));
};
